import { createFileRoute, Link } from "@tanstack/react-router";
import { LegalLayout } from "@/components/legal/LegalLayout";

export const Route = createFileRoute("/cookies")({
  head: () => ({
    meta: [
      { title: "Política de Cookies — Zyntra Tickets" },
      {
        name: "description",
        content:
          "Información sobre las cookies utilizadas por Zyntra Tickets: tipos, finalidad, terceros, consentimiento y cómo desactivarlas.",
      },
      { name: "robots", content: "index, follow" },
      { property: "og:title", content: "Política de Cookies — Zyntra Tickets" },
      {
        property: "og:description",
        content:
          "Cómo usamos cookies y tecnologías similares en Zyntra Tickets, y cómo gestionarlas.",
      },
      { property: "og:type", content: "article" },
    ],
  }),
  component: CookiesPage,
});

function CookiesPage() {
  return (
    <LegalLayout
      title="Política de Cookies"
      subtitle="Cómo usamos cookies y tecnologías similares en Zyntra Tickets, y cómo podés gestionarlas."
      lastUpdated="23 de abril de 2026"
    >
      <section>
        <h2>1. ¿Qué son las cookies?</h2>
        <p>
          Las cookies son pequeños archivos de texto que un sitio web almacena en tu
          navegador cuando lo visitás. Permiten recordar información sobre tu visita,
          como tu sesión iniciada, tus preferencias o tu actividad en la Plataforma,
          para ofrecerte una experiencia más eficiente y segura.
        </p>
        <p>
          Junto con las cookies podemos utilizar tecnologías similares como{" "}
          <em>localStorage</em>, <em>sessionStorage</em>, identificadores de
          dispositivo y píxeles, las cuales se rigen por esta misma política.
        </p>
      </section>

      <section>
        <h2>2. Tipos de cookies que utilizamos</h2>

        <h3>2.1 Cookies estrictamente necesarias (técnicas)</h3>
        <p>
          Imprescindibles para el funcionamiento de la Plataforma. <strong>No requieren consentimiento</strong>{" "}
          conforme a la normativa aplicable.
        </p>
        <ul>
          <li>Mantenimiento de la sesión iniciada.</li>
          <li>Tokens de seguridad y prevención de fraude (CSRF, rate limiting).</li>
          <li>Persistencia del carrito de compra y holds de inventario.</li>
        </ul>

        <h3>2.2 Cookies funcionales</h3>
        <p>Mejoran la experiencia recordando preferencias.</p>
        <ul>
          <li>Idioma y zona horaria.</li>
          <li>Preferencias de visualización (tema, vistas guardadas).</li>
          <li>Tenant o marca activa cuando navegás entre organizadores.</li>
        </ul>

        <h3>2.3 Cookies analíticas</h3>
        <p>
          Nos ayudan a entender de forma agregada cómo se usa la Plataforma para
          mejorarla. La información recolectada es estadística y, cuando es posible,
          anónima.
        </p>
        <ul>
          <li>Páginas más visitadas y tiempos de carga.</li>
          <li>Errores y caídas de rendimiento.</li>
        </ul>

        <h3>2.4 Cookies de terceros</h3>
        <p>
          Algunas funcionalidades dependen de proveedores externos que pueden
          establecer sus propias cookies, regidas por sus respectivas políticas:
        </p>
        <ul>
          <li>
            <strong>Stripe</strong> — procesamiento de pagos y prevención de fraude.{" "}
            <a
              href="https://stripe.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Política de Stripe
            </a>
            .
          </li>
          <li>
            <strong>Google</strong> — autenticación mediante Google OAuth y fuentes
            tipográficas.{" "}
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Política de Google
            </a>
            .
          </li>
        </ul>
        <p>
          <strong>No utilizamos cookies publicitarias ni de seguimiento entre sitios</strong>{" "}
          con fines de marketing.
        </p>
      </section>

      <section>
        <h2>3. Consentimiento</h2>
        <p>
          Las cookies estrictamente necesarias se cargan por defecto, ya que sin
          ellas la Plataforma no puede funcionar. Para el resto de cookies podés
          aceptar, rechazar o configurar tus preferencias en cualquier momento desde
          tu navegador.
        </p>
      </section>

      <section>
        <h2>4. Cómo desactivar las cookies</h2>
        <p>
          Podés bloquear o eliminar las cookies desde la configuración de tu
          navegador. Te dejamos enlaces oficiales:
        </p>
        <ul>
          <li>
            <a
              href="https://support.google.com/chrome/answer/95647"
              target="_blank"
              rel="noopener noreferrer"
            >
              Google Chrome
            </a>
          </li>
          <li>
            <a
              href="https://support.mozilla.org/es/kb/Borrar%20cookies"
              target="_blank"
              rel="noopener noreferrer"
            >
              Mozilla Firefox
            </a>
          </li>
          <li>
            <a
              href="https://support.apple.com/es-es/guide/safari/sfri11471/mac"
              target="_blank"
              rel="noopener noreferrer"
            >
              Safari
            </a>
          </li>
          <li>
            <a
              href="https://support.microsoft.com/es-es/microsoft-edge"
              target="_blank"
              rel="noopener noreferrer"
            >
              Microsoft Edge
            </a>
          </li>
        </ul>
        <p>
          Tené en cuenta que si desactivás las cookies estrictamente necesarias,
          algunas funcionalidades —como iniciar sesión o completar una compra— podrían
          dejar de funcionar correctamente.
        </p>
      </section>

      <section>
        <h2>5. Cambios en esta política</h2>
        <p>
          Podemos actualizar esta política para reflejar cambios técnicos o
          normativos. La fecha de última actualización aparece al inicio de este
          documento.
        </p>
      </section>

      <section>
        <h2>6. Más información</h2>
        <p>
          Para conocer cómo tratamos tus datos personales, consultá nuestra{" "}
          <Link to="/privacy">Política de Privacidad</Link> y nuestros{" "}
          <Link to="/terms">Términos y Condiciones</Link>. Para cualquier consulta:{" "}
          <a href="mailto:legal@zyntratickets.com">legal@zyntratickets.com</a>.
        </p>
      </section>
    </LegalLayout>
  );
}
